import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import debounce from 'lodash.debounce';
import { AuthContext } from '../context/AuthContext';
import '../css/ListaDeCompras.css';

const filters = [
  { value: 'all', label: 'Todos' },
  { value: 'checked', label: 'Marcados' },
  { value: 'unchecked', label: 'Pendentes' },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);

const sanitizeNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const sanitizeQuantity = (value, isWeight = false) => {
  const minimum = isWeight ? 0.01 : 1;
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return minimum;
  }

  return parsedValue < minimum ? minimum : parsedValue;
};

const buildUpdatedItem = (item, changes = {}, isWeight = false) => {
  const quantidade = sanitizeQuantity(
    changes.quantidade ?? item.quantidade,
    isWeight
  );
  const preco = sanitizeNumber(changes.preco ?? item.preco, 0);

  return {
    ...item,
    ...changes,
    quantidade,
    preco,
    total: quantidade * preco,
  };
};

const ShoppingItemRow = ({
  item,
  measureType = 'unidade',
  onToggleChecked,
  onChangeField,
  onDelete,
}) => {
  const isWeight = measureType === 'peso';

  return (
    <div className={`shopping-item ${item.checked ? 'is-checked' : ''}`}>
      <div className="shopping-item__lead">
        <label className="check-toggle" aria-label={`Marcar ${item.nome || 'item'}`}>
          <input type="checkbox" checked={item.checked} onChange={onToggleChecked} />
          <span className="check-toggle__mark"></span>
        </label>

        <div className="shopping-item__content">
          <label className="field field--grow">
            <span className="field-label">Item</span>
            <input
              type="text"
              placeholder="Nome do produto"
              value={item.nome || ''}
              onChange={(e) => onChangeField('nome', e.target.value)}
              className="field-input"
            />
          </label>

          <div className="shopping-item__fields">
            <label className="field field--sm">
              <span className="field-label">{isWeight ? 'Peso (kg)' : 'Quantidade'}</span>
              <input
                type="number"
                min={isWeight ? '0.01' : '1'}
                step={isWeight ? '0.01' : '1'}
                value={item.quantidade ?? (isWeight ? 0.01 : 1)}
                onChange={(e) =>
                  onChangeField('quantidade', sanitizeQuantity(e.target.value, isWeight))
                }
                className="field-input"
              />
            </label>

            <label className="field field--sm">
              <span className="field-label">{isWeight ? 'Preço/kg' : 'Preço'}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={item.preco ?? 0}
                onChange={(e) =>
                  onChangeField('preco', sanitizeNumber(e.target.value, 0))
                }
                className="field-input"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="shopping-item__aside">
        <div className="shopping-item__summary">
          <span>Total</span>
          <strong>{formatCurrency(item.total)}</strong>
        </div>

        <button
          type="button"
          className="icon-button danger-button"
          onClick={onDelete}
          aria-label={`Remover ${item.nome || 'item'}`}
        >
          <i className="bi bi-trash3"></i>
        </button>
      </div>
    </div>
  );
};

const ListaDeCompras = () => {
  const [lista, setLista] = useState(null);
  const [filter, setFilter] = useState('all');
  const { authData } = useContext(AuthContext);
  const { token } = authData;
  const { id: listaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [novoItemSemCategoria, setNovoItemSemCategoria] = useState('');
  const [novaCategoria, setNovaCategoria] = useState('');
  const [novoItemNaCategoria, setNovoItemNaCategoria] = useState({});
  const [editCategoryName, setEditCategoryName] = useState({});
  const [editingCategory, setEditingCategory] = useState({});

  useEffect(() => {
    fetchLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaId, token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const socket = io(process.env.REACT_APP_API_URL);
    socket.emit('join', `lista_${listaId}`);

    socket.on('item_adicionado', (item) => {
      setLista((prev) =>
        prev
          ? {
              ...prev,
              itens: [...prev.itens, item],
            }
          : prev
      );
    });

    socket.on('item_atualizado', (itemAtualizado) => {
      setLista((prev) =>
        prev
          ? {
              ...prev,
              itens: prev.itens.map((item) =>
                item._id === itemAtualizado._id ? itemAtualizado : item
              ),
            }
          : prev
      );
    });

    socket.on('item_removido', ({ itemId }) => {
      setLista((prev) =>
        prev
          ? {
              ...prev,
              itens: prev.itens.filter((item) => item._id !== itemId),
            }
          : prev
      );
    });

    socket.on('lista_atualizada', (updatedLista) => {
      setLista(updatedLista);
    });

    socket.on('lista_removida', ({ id }) => {
      if (id === listaId) {
        navigate('/listas', {
          replace: true,
          state: {
            feedbackType: 'info',
            feedbackText: 'Esta lista foi removida.',
          },
        });
      }
    });

    socket.on('categoria_adicionada', (novaCategoriaCriada) => {
      setLista((prev) =>
        prev
          ? {
              ...prev,
              categorias: [...prev.categorias, novaCategoriaCriada],
            }
          : prev
      );
    });

    socket.on('item_categoria_adicionado', ({ catId, item }) => {
      setLista((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          categorias: prev.categorias.map((cat) =>
            cat._id === catId ? { ...cat, itens: [...cat.itens, item] } : cat
          ),
        };
      });
    });

    socket.on('item_categoria_atualizado', ({ catId, item }) => {
      setLista((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          categorias: prev.categorias.map((cat) =>
            cat._id === catId
              ? {
                  ...cat,
                  itens: cat.itens.map((currentItem) =>
                    currentItem._id === item._id ? item : currentItem
                  ),
                }
              : cat
          ),
        };
      });
    });

    socket.on('item_categoria_removido', ({ catId, itemId }) => {
      setLista((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          categorias: prev.categorias.map((cat) =>
            cat._id === catId
              ? {
                  ...cat,
                  itens: cat.itens.filter((item) => item._id !== itemId),
                }
              : cat
          ),
        };
      });
    });

    socket.on('categoria_atualizada', (updatedCategoria) => {
      setLista((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          categorias: prev.categorias.map((cat) =>
            cat._id === updatedCategoria._id ? updatedCategoria : cat
          ),
        };
      });
    });

    socket.on('categoria_removida', ({ catId }) => {
      setLista((prev) =>
        prev
          ? {
              ...prev,
              categorias: prev.categorias.filter((cat) => cat._id !== catId),
            }
          : prev
      );
    });

    return () => {
      socket.emit('leave', `lista_${listaId}`);
      socket.disconnect();
    };
  }, [listaId, navigate, token]);

  const fetchLista = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/listas/${listaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLista(res.data);
      setFeedback(null);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível carregar a lista.',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (items) => {
    if (filter === 'checked') {
      return items.filter((item) => item.checked);
    }

    if (filter === 'unchecked') {
      return items.filter((item) => !item.checked);
    }

    return items;
  };

  const addItemSemCategoria = async () => {
    if (!novoItemSemCategoria.trim()) {
      setFeedback({
        type: 'error',
        text: 'Digite um nome de item para adicionar.',
      });
      return;
    }

    const newItem = {
      nome: novoItemSemCategoria,
      quantidade: 1,
      preco: 0,
      checked: false,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/listas/${listaId}/itens`, newItem, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNovoItemSemCategoria('');
      setFeedback(null);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível adicionar o item.',
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateItem = useCallback(
    debounce(async (itemId, updatedItem) => {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/listas/${listaId}/itens/${itemId}`,
          updatedItem,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        setFeedback({
          type: 'error',
          text: err.response?.data?.msg || 'Não foi possível atualizar o item.',
        });
      }
    }, 500),
    [listaId, token]
  );

  const handleInputChange = (itemId, key, value) => {
    let updatedItem = null;

    setLista((prev) => {
      if (!prev) {
        return prev;
      }

      const novosItens = prev.itens.map((item) => {
        if (item._id !== itemId) {
          return item;
        }

        updatedItem = buildUpdatedItem(item, { [key]: value });
        return updatedItem;
      });

      return { ...prev, itens: novosItens };
    });

    if (updatedItem) {
      debouncedUpdateItem(updatedItem._id, updatedItem);
    }
  };

  const toggleChecked = (itemId) => {
    let updatedItem = null;

    setLista((prev) => {
      if (!prev) {
        return prev;
      }

      const novosItens = prev.itens.map((item) => {
        if (item._id !== itemId) {
          return item;
        }

        updatedItem = buildUpdatedItem(item, { checked: !item.checked });
        return updatedItem;
      });

      return {
        ...prev,
        itens: novosItens,
      };
    });

    if (updatedItem) {
      debouncedUpdateItem(updatedItem._id, updatedItem);
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/listas/${listaId}/itens/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível remover o item.',
      });
    }
  };

  const addCategory = async () => {
    if (!novaCategoria.trim()) {
      setFeedback({
        type: 'error',
        text: 'Digite um nome de categoria para adicionar.',
      });
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias`,
        { nome: novaCategoria },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNovaCategoria('');
      setFeedback(null);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível criar a categoria.',
      });
    }
  };

  const addItemToCategory = async (catId) => {
    const nomeItem = novoItemNaCategoria[catId];

    if (!nomeItem || !nomeItem.trim()) {
      setFeedback({
        type: 'error',
        text: 'Digite um nome de item para esta categoria.',
      });
      return;
    }

    const newItem = {
      nome: nomeItem,
      quantidade: 1,
      preco: 0,
      checked: false,
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias/${catId}/itens`,
        newItem,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNovoItemNaCategoria((prev) => ({ ...prev, [catId]: '' }));
      setFeedback(null);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível adicionar o item.',
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateItemInCategory = useCallback(
    debounce(async (catId, itemId, updatedItem) => {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias/${catId}/itens/${itemId}`,
          updatedItem,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        setFeedback({
          type: 'error',
          text: err.response?.data?.msg || 'Não foi possível atualizar o item.',
        });
      }
    }, 500),
    [listaId, token]
  );

  const handleCategoryItemChange = (catId, itemId, key, value, isWeight = false) => {
    let updatedItem = null;

    setLista((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedCategorias = prev.categorias.map((cat) => {
        if (cat._id !== catId) {
          return cat;
        }

        return {
          ...cat,
          itens: cat.itens.map((item) => {
            if (item._id !== itemId) {
              return item;
            }

            updatedItem = buildUpdatedItem(item, { [key]: value }, isWeight);
            return updatedItem;
          }),
        };
      });

      return { ...prev, categorias: updatedCategorias };
    });

    if (updatedItem) {
      debouncedUpdateItemInCategory(catId, itemId, updatedItem);
    }
  };

  const toggleCategoryItemChecked = (catId, itemId, isWeight = false) => {
    let updatedItem = null;

    setLista((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedCategorias = prev.categorias.map((cat) => {
        if (cat._id !== catId) {
          return cat;
        }

        return {
          ...cat,
          itens: cat.itens.map((item) => {
            if (item._id !== itemId) {
              return item;
            }

            updatedItem = buildUpdatedItem(item, { checked: !item.checked }, isWeight);
            return updatedItem;
          }),
        };
      });

      return { ...prev, categorias: updatedCategorias };
    });

    if (updatedItem) {
      debouncedUpdateItemInCategory(catId, itemId, updatedItem);
    }
  };

  const deleteItemInCategory = async (catId, itemId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias/${catId}/itens/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível remover o item.',
      });
    }
  };

  const toggleEditCategory = (catId, currentName) => {
    const isEditing = editingCategory[catId];
    setEditingCategory((prev) => ({ ...prev, [catId]: !isEditing }));
    if (!isEditing) {
      setEditCategoryName((prev) => ({ ...prev, [catId]: currentName }));
    }
  };

  const handleCategoryNameChange = (catId, newValue) => {
    setEditCategoryName((prev) => ({ ...prev, [catId]: newValue }));
  };

  const updateCategoryName = async (catId) => {
    const newName = editCategoryName[catId];

    if (!newName || !newName.trim()) {
      setFeedback({
        type: 'error',
        text: 'O nome da categoria não pode estar vazio.',
      });
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias/${catId}`,
        { nome: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingCategory((prev) => ({ ...prev, [catId]: false }));
      setFeedback(null);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível renomear a categoria.',
      });
    }
  };

  const deleteCategory = async (catId) => {
    if (
      !window.confirm(
        'Tem certeza que deseja excluir esta categoria e todos os itens dentro dela?'
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias/${catId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível excluir a categoria.',
      });
    }
  };

  const toggleMeasureType = async (catId, currentMeasure) => {
    const newMeasure = currentMeasure === 'unidade' ? 'peso' : 'unidade';

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/listas/${listaId}/categorias/${catId}/tipo-medida`,
        { tipoDeMedida: newMeasure },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Erro ao alterar o tipo de medida.',
      });
    }
  };

  const calculateTotal = () => {
    if (!lista) {
      return 0;
    }

    const totalSemCategoria = lista.itens.reduce(
      (sum, item) => sum + (Number(item.total) || 0),
      0
    );

    const totalCategorias = lista.categorias.reduce(
      (sum, categoria) =>
        sum +
        categoria.itens.reduce(
          (categoryTotal, item) => categoryTotal + (Number(item.total) || 0),
          0
        ),
      0
    );

    return totalSemCategoria + totalCategorias;
  };

  useEffect(() => {
    return () => {
      debouncedUpdateItem.cancel();
      debouncedUpdateItemInCategory.cancel();
    };
  }, [debouncedUpdateItem, debouncedUpdateItemInCategory]);

  if (loading) {
    return (
      <section className="page-section">
        <div className="surface-card page-status">Carregando lista...</div>
      </section>
    );
  }

  if (!lista) {
    return (
      <section className="page-section">
        <div className="surface-card empty-state">
          <i className="bi bi-exclamation-circle"></i>
          <h2>Não foi possível abrir esta lista</h2>
          <p>{feedback?.text || 'Tente novamente em instantes.'}</p>
          <Link to="/listas" className="primary-button">
            Voltar para minhas listas
          </Link>
        </div>
      </section>
    );
  }

  const filteredTopLevelItems = applyFilter(lista.itens);
  const allItems = [
    ...lista.itens,
    ...lista.categorias.flatMap((categoria) => categoria.itens || []),
  ];
  const completedItems = allItems.filter((item) => item.checked).length;
  const totalItems = allItems.length;
  const progress = totalItems ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <section className="page-section">
      <div className="surface-card list-hero">
        <div>
          <Link to="/listas" className="inline-link">
            <i className="bi bi-arrow-left"></i>
            Voltar para minhas listas
          </Link>
          <span className="eyebrow">Lista ativa</span>
          <h1 className="section-title">{lista.nome}</h1>
          <p className="section-copy">
            Edite itens, acompanhe os totais e filtre o que já foi comprado sem
            perder o contexto da lista inteira.
          </p>
        </div>

        <div className="stats-grid stats-grid--compact">
          <div className="metric-card">
            <span className="metric-label">Itens</span>
            <strong className="metric-value">{totalItems}</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Concluídos</span>
            <strong className="metric-value">{completedItems}</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Progresso</span>
            <strong className="metric-value">{progress}%</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Valor total</span>
            <strong className="metric-value">{formatCurrency(calculateTotal())}</strong>
          </div>
        </div>
      </div>

      {feedback && (
        <div className={`notice notice--${feedback.type}`} role="status">
          {feedback.text}
        </div>
      )}

      <div className="list-tool-grid">
        <div className="surface-card list-tool-card">
          <div className="section-heading">
            <span className="eyebrow">Adição rápida</span>
            <h2 className="section-subtitle">Item sem categoria</h2>
          </div>
          <div className="inline-form">
            <input
              type="text"
              placeholder="Ex.: Café, arroz, detergente"
              className="field-input"
              value={novoItemSemCategoria}
              onChange={(e) => setNovoItemSemCategoria(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addItemSemCategoria()}
            />
            <button className="primary-button" type="button" onClick={addItemSemCategoria}>
              Adicionar item
            </button>
          </div>
        </div>

        <div className="surface-card list-tool-card">
          <div className="section-heading">
            <span className="eyebrow">Organização</span>
            <h2 className="section-subtitle">Nova categoria</h2>
          </div>
          <div className="inline-form">
            <input
              type="text"
              className="field-input"
              placeholder="Ex.: Hortifruti, limpeza, bebidas"
              value={novaCategoria}
              onChange={(e) => setNovaCategoria(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCategory()}
            />
            <button className="subtle-button" type="button" onClick={addCategory}>
              Criar categoria
            </button>
          </div>
        </div>

        <div className="surface-card list-tool-card">
          <div className="section-heading">
            <span className="eyebrow">Filtro</span>
            <h2 className="section-subtitle">Visualize do seu jeito</h2>
          </div>
          <div className="chip-group">
            {filters.map((filterOption) => (
              <button
                key={filterOption.value}
                type="button"
                className={`chip-button ${
                  filter === filterOption.value ? 'is-active' : ''
                }`}
                onClick={() => setFilter(filterOption.value)}
              >
                {filterOption.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="shopping-layout">
        <section className="surface-card shopping-card">
          <div className="section-heading section-heading--row">
            <div>
              <span className="eyebrow">Base da lista</span>
              <h2 className="section-subtitle">Itens sem categoria</h2>
            </div>
            <span className="toolbar-badge">{filteredTopLevelItems.length} visíveis</span>
          </div>

          {filteredTopLevelItems.length > 0 ? (
            <div className="item-stack">
              {filteredTopLevelItems.map((item) => (
                <ShoppingItemRow
                  key={item._id}
                  item={item}
                  onToggleChecked={() => toggleChecked(item._id)}
                  onChangeField={(key, value) => handleInputChange(item._id, key, value)}
                  onDelete={() => deleteItem(item._id)}
                />
              ))}
            </div>
          ) : (
            <div className="item-empty">
              Nenhum item sem categoria corresponde ao filtro atual.
            </div>
          )}
        </section>

        <section className="category-section">
          <div className="section-heading section-heading--row">
            <div>
              <span className="eyebrow">Categorias</span>
              <h2 className="section-subtitle">Itens organizados por setor</h2>
            </div>
            <span className="toolbar-badge">{lista.categorias.length} categorias</span>
          </div>

          {lista.categorias.length > 0 ? (
            <div className="category-grid">
              {lista.categorias.map((cat) => {
                const isEditing = editingCategory[cat._id] || false;
                const filteredCatItems = applyFilter(cat.itens);
                const isWeight = cat.tipoDeMedida === 'peso';

                if (filter !== 'all' && filteredCatItems.length === 0) {
                  return null;
                }

                return (
                  <article className="surface-card category-card" key={cat._id}>
                    <div className="category-card__header">
                      {isEditing ? (
                        <div className="inline-panel">
                          <label className="field">
                            <span className="field-label">Nome da categoria</span>
                            <input
                              type="text"
                              className="field-input"
                              value={editCategoryName[cat._id] || ''}
                              onChange={(e) =>
                                handleCategoryNameChange(cat._id, e.target.value)
                              }
                              onKeyDown={(e) =>
                                e.key === 'Enter' && updateCategoryName(cat._id)
                              }
                            />
                          </label>
                          <div className="inline-actions">
                            <button
                              type="button"
                              className="primary-button button-compact"
                              onClick={() => updateCategoryName(cat._id)}
                            >
                              Salvar
                            </button>
                            <button
                              type="button"
                              className="subtle-button button-compact"
                              onClick={() =>
                                setEditingCategory((prev) => ({
                                  ...prev,
                                  [cat._id]: false,
                                }))
                              }
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div>
                            <h3>{cat.nome}</h3>
                            <p>
                              {filteredCatItems.length} itens visíveis de {cat.itens.length}
                            </p>
                          </div>
                          <div className="category-card__actions">
                            <button
                              type="button"
                              className="subtle-button button-compact"
                              onClick={() => toggleMeasureType(cat._id, cat.tipoDeMedida)}
                              title={
                                isWeight ? 'Mudar para unidade' : 'Mudar para peso'
                              }
                            >
                              <i
                                className={`bi ${
                                  isWeight ? 'bi-rulers' : 'bi-123'
                                }`}
                              ></i>
                              {isWeight ? 'Peso' : 'Unidade'}
                            </button>
                            <button
                              type="button"
                              className="subtle-button button-compact"
                              onClick={() => toggleEditCategory(cat._id, cat.nome)}
                            >
                              Renomear
                            </button>
                            <button
                              type="button"
                              className="danger-button button-compact"
                              onClick={() => deleteCategory(cat._id)}
                            >
                              Excluir
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="inline-form inline-form--tight">
                      <input
                        type="text"
                        placeholder="Adicionar item à categoria"
                        className="field-input"
                        value={novoItemNaCategoria[cat._id] || ''}
                        onChange={(e) =>
                          setNovoItemNaCategoria((prev) => ({
                            ...prev,
                            [cat._id]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => e.key === 'Enter' && addItemToCategory(cat._id)}
                      />
                      <button
                        type="button"
                        className="primary-button button-compact"
                        onClick={() => addItemToCategory(cat._id)}
                      >
                        Adicionar
                      </button>
                    </div>

                    {filteredCatItems.length > 0 ? (
                      <div className="item-stack">
                        {filteredCatItems.map((item) => (
                          <ShoppingItemRow
                            key={item._id}
                            item={item}
                            measureType={cat.tipoDeMedida}
                            onToggleChecked={() =>
                              toggleCategoryItemChecked(cat._id, item._id, isWeight)
                            }
                            onChangeField={(key, value) =>
                              handleCategoryItemChange(
                                cat._id,
                                item._id,
                                key,
                                value,
                                isWeight
                              )
                            }
                            onDelete={() => deleteItemInCategory(cat._id, item._id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="item-empty">
                        Nenhum item nesta categoria corresponde ao filtro atual.
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="surface-card item-empty">
              Você ainda não criou categorias para esta lista.
            </div>
          )}
        </section>
      </div>
    </section>
  );
};

export default ListaDeCompras;
